import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import { PAGE_SIZES } from "./constants";
import useViewport from "../../../../hooks/useViewport";
import { useNormalizeClass } from "../../../../hooks/useNormalizeClass";
import Icon from "../../../../components/Icon";
import CarouselCardList from "./components/CarouselCardList/component";
import CarouselPlaceholder from "./components/CarouselPlaceholder/component";

const Carousel = ({ layout = {}, items = [], featureAttributionData = {} }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const sliderRefArray = useRef([]);
    const ref = useRef();
    const scrollRef = useRef();
    const viewport = useViewport(ref, "viewportLarge");
    let pageSize = PAGE_SIZES.LARGE;

    switch (viewport) {
        case "viewportSmall":
            pageSize = PAGE_SIZES.SMALL;
            break;
        case "viewportMedium":
            pageSize = PAGE_SIZES.MED;
            break;
        default:
            pageSize = PAGE_SIZES.LARGE;
    }

    const NUMBER_OF_CARDS = items.length;
    const isLoading = NUMBER_OF_CARDS === 0;
    let MAX_PAGES = Math.ceil(NUMBER_OF_CARDS / pageSize);
    if (isLoading) {
        pageSize = PAGE_SIZES.LARGE;
        MAX_PAGES = 1;
    }

    useEffect(() => {
        // Correction whenever resizing, calculations of the current page
        // needs to always be lower than the maximum number of pages per viewport
        if (currentPage > MAX_PAGES) {
            setCurrentPage(MAX_PAGES);
        }
    }, [viewport]);

    useEffect(() => {
        // When user navigates to a new url, reset the page to 1
        setCurrentPage(1);
        if (scrollRef?.current?.scroll)
            scrollRef?.current?.scroll({
                left: 0,
                behavior: "smooth",
            });
    }, [layout.carouselTitle]);

    const carouselId = useNormalizeClass("carousel", layout.carouselTitle);

    const scrollToPage = (page) => {
        const index = page - 1;
        if (sliderRefArray.current[index])
            sliderRefArray.current[index].scrollIntoView({ block: "nearest" });
    };

    const showPrevious = () => {
        if (currentPage > 1) {
            const newCurrentPage = currentPage - 1;

            scrollToPage(newCurrentPage);
            setCurrentPage(newCurrentPage);
        }
    };

    const showNext = () => {
        if (currentPage < MAX_PAGES) {
            const newCurrentPage = currentPage + 1;

            scrollToPage(newCurrentPage);
            setCurrentPage(newCurrentPage);
        }
    };

    const prevEnabled = currentPage > 1;
    const nextEnabled = currentPage < MAX_PAGES;

    const carouselCardListProps = {
        items,
        featureAttributionData,
        maxPages: MAX_PAGES,
        pageSize,
        carouselTitle: layout.carouselTitle,
    };

    return (
        <div
            className="carouselWrapper"
            role="region"
            aria-labelledby={carouselId}
            ref={ref}
            data-cy="carouselWrapper"
        >
            <div className="carouselTop">
                <h2 className="header" id={carouselId}>
                    {layout.carouselTitle}
                    <span className="a11y">Carousel</span>
                </h2>
                <div
                    className="carouselNav"
                    aria-hidden="true"
                    data-cy="carouselNav"
                >
                    <span className="carouselIndicator">
                        Page {currentPage} of {MAX_PAGES}
                    </span>
                </div>
            </div>
            <div className="carousel sclt-carousel">
                <button
                    className={`prev ${prevEnabled ? "show" : ""}`}
                    onClick={showPrevious}
                    aria-label="previous"
                    data-cy="previous"
                >
                    <Icon type="chevron" />
                </button>
                <div className="itemContainer">
                    <div
                        ref={scrollRef}
                        className="itemList"
                        data-testid="item-list"
                        data-cy="item-list"
                    >
                        {isLoading ? (
                            <CarouselPlaceholder />
                        ) : (
                            <CarouselCardList
                                {...carouselCardListProps}
                                ref={sliderRefArray}
                            />
                        )}
                    </div>
                </div>
                <button
                    className={`next ${nextEnabled ? "show" : ""}`}
                    onClick={showNext}
                    aria-label="next"
                    data-cy="next"
                >
                    <Icon type="chevron" />
                </button>
                {nextEnabled && <span className="a11y">End of carousel</span>}
            </div>
        </div>
    );
};

Carousel.propTypes = {
    items: PropTypes.arrayOf(PropTypes.object),
    layout: PropTypes.shape({
        carouselTitle: PropTypes.string,
    }),
    featureAttributionData: PropTypes.shape({
        instance: PropTypes.string,
    }),
};

export default Carousel;
